"use client";

import { Search, X } from "lucide-react";
import {
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { Button } from "../../../elements/button/button";
import { TextD2C } from "../../../elements/text-d2c/text";
import { cn } from "../../../lib/utils";
import { mainNavigationClasses } from "../main-navigation.constants";

export interface NavigationSearchSuggestion {
  id: string;
  label: string;
  href: string;
}

export interface NavigationSearchProps {
  /** Search results page the form submits to, e.g. `/ch/de/search`. */
  action: string;
  /** Query parameter the search results page reads. */
  queryParam?: string;
  /**
   * Loads the live suggestions of a query. The signal aborts as soon as the
   * query changes. Without it the search only submits to the results page.
   */
  fetchSuggestions?: (
    query: string,
    signal: AbortSignal
  ) => Promise<NavigationSearchSuggestion[]>;
  microcopies: {
    /** Accessible name of the search field. */
    label: string;
    placeholder: string;
    submit: string;
    clear: string;
    /** Title of the suggestion list, e.g. "Products". */
    suggestions: string;
    loading: string;
    noResults: string;
    /** Announced to screen readers; `{count}` is replaced by the number of suggestions. */
    resultCount: string;
    /** Link to the results page below the suggestions. */
    showAll: string;
  };
}

/** Suggestions load from this many characters on, like the previous header search. */
export const NAVIGATION_SEARCH_MIN_QUERY_LENGTH = 3;
/** Waits for a typing pause before loading suggestions. */
export const NAVIGATION_SEARCH_DEBOUNCE_MS = 300;

interface SuggestionResult {
  query: string;
  suggestions: NavigationSearchSuggestion[];
}

/**
 * Search form of the header with live text suggestions. The arrow keys move
 * between the field and the suggestions; Enter submits the query to the
 * results page unless a suggestion is focused.
 */
export function NavigationSearch(props: Readonly<NavigationSearchProps>) {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState<SuggestionResult>({
    query: "",
    suggestions: [],
  });
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // Read through a ref, so that a new function on every render of the app does
  // not restart a pending request.
  const fetchSuggestionsRef = useRef(props.fetchSuggestions);
  const queryParam = props.queryParam ?? "text";
  const trimmedQuery = query.trim();
  const showsSuggestions =
    Boolean(props.fetchSuggestions) &&
    trimmedQuery.length >= NAVIGATION_SEARCH_MIN_QUERY_LENGTH;
  const isLoading = showsSuggestions && result.query !== trimmedQuery;

  useEffect(() => {
    fetchSuggestionsRef.current = props.fetchSuggestions;
  }, [props.fetchSuggestions]);

  useEffect(() => {
    const fetchSuggestions = fetchSuggestionsRef.current;
    if (
      !fetchSuggestions ||
      trimmedQuery.length < NAVIGATION_SEARCH_MIN_QUERY_LENGTH
    ) {
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      let suggestions: NavigationSearchSuggestion[] = [];
      try {
        suggestions = await fetchSuggestions(trimmedQuery, controller.signal);
      } catch {
        // A failed request shows no suggestions, like the previous header search.
      }
      if (!controller.signal.aborted) {
        setResult({ query: trimmedQuery, suggestions });
      }
    }, NAVIGATION_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [trimmedQuery]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    if (!trimmedQuery) {
      event.preventDefault();
      inputRef.current?.focus();
    }
  }

  function handleClear() {
    setQuery("");
    inputRef.current?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
      return;
    }

    const links = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>("a[href]") ?? []
    );
    const input = inputRef.current;
    if (!(input && links.length)) {
      return;
    }

    const targets = [input, ...links];
    const currentIndex = targets.indexOf(event.target as HTMLElement);
    if (currentIndex === -1) {
      return;
    }

    event.preventDefault();
    const nextIndex =
      event.key === "ArrowDown"
        ? Math.min(currentIndex + 1, targets.length - 1)
        : Math.max(currentIndex - 1, 0);
    targets[nextIndex]?.focus();
  }

  const suggestions = isLoading ? [] : result.suggestions;
  const showAllHref = `${props.action}?${new URLSearchParams({
    [queryParam]: trimmedQuery,
  })}`;

  return (
    // The mobile design keeps the field at the bottom, within thumb reach,
    // while the results scroll above it. It stays first for screen readers.
    <div
      className="flex flex-col-reverse gap-6 max-lg:h-full lg:flex-col lg:gap-10"
      onKeyDown={handleKeyDown}
    >
      <search className="shrink-0">
        <form
          action={props.action}
          className="flex h-13 items-center gap-2 rounded-sm border border-alabastergrey bg-white pr-2 pl-1 focus-within:border-black"
          method="get"
          onSubmit={handleSubmit}
        >
          <Button
            aria-label={props.microcopies.submit}
            className="rounded-full"
            mode="D2C"
            size="icon"
            type="submit"
            variant="ghost"
          >
            <Search aria-hidden="true" />
          </Button>
          <label className="sr-only" htmlFor={inputId}>
            {props.microcopies.label}
          </label>
          <input
            autoComplete="off"
            // iOS zooms into fields below 16px, so the design's 12px only applies on desktop.
            className="h-full min-w-0 flex-1 bg-transparent font-brand text-[16px] text-black outline-none placeholder:text-black/60 lg:text-[12px] lg:leading-[17px] [&::-webkit-search-cancel-button]:appearance-none"
            enterKeyHint="search"
            id={inputId}
            name={queryParam}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={props.microcopies.placeholder}
            ref={inputRef}
            type="search"
            value={query}
          />
          {query ? (
            <Button
              aria-label={props.microcopies.clear}
              className="rounded-full"
              mode="D2C"
              onClick={handleClear}
              size="icon-sm"
              type="button"
              variant="ghost"
            >
              <X aria-hidden="true" />
            </Button>
          ) : null}
        </form>
      </search>

      <div
        className="flex min-h-0 flex-1 flex-col gap-10 max-lg:overflow-y-auto lg:contents"
        data-testid="navigation-search-results"
      >
        {/* Rendered before its text changes, so that screen readers announce
            it. Only loading and empty states show; the count is announced. */}
        <output
          className={cn(
            "font-brand text-[14px] leading-5",
            !(showsSuggestions && suggestions.length === 0) && "sr-only"
          )}
        >
          {showsSuggestions
            ? getStatusText(props.microcopies, isLoading, suggestions.length)
            : null}
        </output>

        {suggestions.length > 0 && showsSuggestions ? (
          <section
            aria-labelledby={`${inputId}-suggestions`}
            className="flex flex-col gap-4"
          >
            <TextD2C
              id={`${inputId}-suggestions`}
              style="no-mb"
              tagName="h2"
              variant="h5"
            >
              {props.microcopies.suggestions}
            </TextD2C>
            <ul className="flex flex-col gap-4" ref={listRef}>
              {suggestions.map((suggestion) => (
                <li key={suggestion.id}>
                  <Button
                    className={mainNavigationClasses.link}
                    href={suggestion.href}
                    mode="D2C"
                    unstyled={true}
                  >
                    {suggestion.label}
                  </Button>
                </li>
              ))}
            </ul>
            <Button
              className={mainNavigationClasses.cta}
              href={showAllHref}
              mode="D2C"
              unstyled={true}
            >
              {props.microcopies.showAll}
            </Button>
          </section>
        ) : null}
      </div>
    </div>
  );
}

function getStatusText(
  microcopies: NavigationSearchProps["microcopies"],
  isLoading: boolean,
  count: number
): string {
  if (isLoading) {
    return microcopies.loading;
  }
  if (count === 0) {
    return microcopies.noResults;
  }
  return microcopies.resultCount.replace("{count}", String(count));
}
